/* Users / Team: the A2 quick filters, the toolbar and the users table. */
import React from 'react';
import PropTypes from 'prop-types';
import { Button, DataTable, IconButton } from '../ui';
import {
    findNode, isAdminRole, PERSPECTIVE_LABEL, ROLE_LABEL, TYPE_LABEL, userGuard, userKind,
} from '../../utils/adminConsole';
import {
    AccessChip, Alert, AppSwitch, Avatar, KindChip, RoleText, SectionHead, StatusChip,
} from './bits';
import { presetSummary } from '../../utils/rolePresets';
import {
    KeyIcon, OpenIcon, PlusIcon, RefreshIcon, SearchIcon,
} from './icons';

export const NO_FILTERS = { quick: 'all', role: 'All', status: 'All' };

/** The A2 tiles for the users in view (super: staff / dealer / access; dealer: team). */
export function quickDefs(users, { isSuper, summaryOf, orgs }) {
    const count = (fn) => users.filter(fn).length;
    if (isSuper) {
        return [
            { k: 'all', l: 'All users', n: users.length, tone: 'ink' },
            { k: 'staff', l: 'Fusable staff', n: count((u) => userKind(u, orgs) === 'staff'), tone: 'hunter' },
            { k: 'dealer', l: 'Dealer users', n: count((u) => userKind(u, orgs) !== 'staff'), tone: 'sage' },
            { k: 'admins', l: 'Admins', n: count((u) => isAdminRole(u.role)), tone: 'hunter' },
            { k: 'ro', l: 'Read-only', n: count((u) => summaryOf(u).level === 'ro'), tone: 'amber' },
            { k: 'none', l: 'No dealership access', n: count((u) => summaryOf(u).level === 'none'), tone: 'grey' },
        ];
    }
    return [
        { k: 'all', l: 'Team members', n: users.length, tone: 'ink' },
        { k: 'admins', l: 'Admins', n: count((u) => isAdminRole(u.role)), tone: 'hunter' },
        { k: 'appoff', l: 'IronSight off', n: count((u) => !u.apps?.pricepath), tone: 'grey' },
        { k: 'suspended', l: 'Suspended', n: count((u) => u.status === 'suspended'), tone: 'red' },
    ];
}

export function applyFilters(users, filters, { orgs, summaryOf }) {
    return users.filter((u) => {
        const q = filters.quick;
        if (q === 'staff' && userKind(u, orgs) !== 'staff') return false;
        if (q === 'dealer' && userKind(u, orgs) === 'staff') return false;
        if (q === 'admins' && !isAdminRole(u.role)) return false;
        if (q === 'ro' && summaryOf(u).level !== 'ro') return false;
        if (q === 'none' && summaryOf(u).level !== 'none') return false;
        if (q === 'appoff' && u.apps?.pricepath) return false;
        if (q === 'suspended' && u.status !== 'suspended') return false;
        if (filters.role !== 'All' && u.role !== filters.role) return false;
        if (filters.status !== 'All' && u.status !== filters.status) return false;
        return true;
    });
}

function UsersSection({
    c, me, filters, setFilters, summaryOf, onOpen, onAdd, onRefresh, selectedId,
}) {
    const { isSuper, orgs, currentNode } = c;
    // Search first (today's), then the A2 tile and the selects.
    const rows = applyFilters(c.filteredUsers, filters, { orgs, summaryOf });
    const filtered = filters.quick !== 'all' || filters.role !== 'All' || filters.status !== 'All';
    const any = Boolean(c.query) || filtered || (isSuper && Boolean(currentNode));
    const clearAll = () => {
        setFilters(NO_FILTERS);
        c.setQuery('');
        if (isSuper && currentNode) c.setScope(null);
    };

    const ctx = { me, isSuper };
    const columns = [
        {
            key: 'user',
            label: 'User',
            render: (u) => {
                const kind = userKind(u, orgs);
                const self = Boolean(me?.email) && u.email === me.email;
                return (
                    <div className="ad2-who">
                        <Avatar user={u} kind={kind} />
                        <div>
                            <div className="ad2-nm">
                                {u.display_name}
                                {self && <span className="ad2-you">YOU</span>}
                                {isSuper && <KindChip kind={kind} />}
                            </div>
                            <div className="ad2-em">{u.email}</div>
                        </div>
                    </div>
                );
            },
        },
        {
            key: 'org',
            label: 'Organization · Role',
            render: (u) => (
                <div className="ad2-org-c">
                    <span>{u.org_name || '—'}</span>
                    {u.org_id && <span className="ad2-persp">{PERSPECTIVE_LABEL[findNode(orgs, u.org_id)?.type] || ''}</span>}
                    <RoleText role={u.role} />
                </div>
            ),
        },
        {
            key: 'preset',
            label: (
                <span title="The role preset a member's inventory columns start from (layout only, never access)">
                    Role preset
                </span>
            ),
            render: (u) => {
                const p = presetSummary(u);
                return (
                    <div className="ad2-preset-cell">
                        <span>{p.assigned || <span className="ad2-muted">—</span>}</span>
                        {p.chose && <span className="ad2-pre-note">user chose: {p.chose}</span>}
                    </div>
                );
            },
        },
        ...(isSuper ? [{
            key: 'access',
            label: (
                <span title="Expected access, derived from where the user is a member. Edit = can create tasks and changes. Read-only = can look only.">
                    Dealership access
                </span>
            ),
            className: 'ad2-accw',
            render: (u) => <AccessChip summary={summaryOf(u)} />,
        }] : []),
        {
            key: 'status',
            label: 'Status · IronSight',
            render: (u) => {
                const guard = userGuard(u, 'app', ctx);
                const on = Boolean(u.apps?.pricepath);
                return (
                    <div className="ad2-stw">
                        <StatusChip status={u.status} />
                        <AppSwitch
                            on={on}
                            label={`IronSight access for ${u.display_name}`}
                            title={guard || (on ? 'Revoke IronSight access' : 'Grant IronSight access')}
                            disabled={c.busyId === u.id || !c.canEdit || Boolean(guard)}
                            onToggle={() => c.patchUser(u, { apps: on ? [] : ['pricepath'] })}
                        />
                    </div>
                );
            },
        },
        {
            key: 'act',
            label: <span className="v2-sr">Actions</span>,
            className: 'ad2-act',
            render: (u) => {
                const guard = userGuard(u, 'reset', ctx);
                return (
                    <span className="ad2-acts">
                        <IconButton
                            label={`Reset password for ${u.display_name}`}
                            disabled={c.busyId === u.id || Boolean(guard)}
                            title={guard || 'Reset password'}
                            onClick={() => c.setResetConfirm(u.id)}
                        >
                            <KeyIcon />
                        </IconButton>
                        <IconButton label={`Open ${u.display_name}`} onClick={() => onOpen(u)}><OpenIcon /></IconButton>
                    </span>
                );
            },
        },
    ];

    let empty = null;
    if (!c.usersLoading && rows.length === 0) {
        let text = 'No users at this level yet.';
        if (c.query) text = 'No users match your search.';
        else if (filtered) text = 'No users match these filters.';
        empty = (
            <div className="ad2-empty">
                <p>{text}</p>
                {any && <Button onClick={clearAll}>Clear filters</Button>}
            </div>
        );
    }

    const tiles = quickDefs(c.users, { isSuper, summaryOf, orgs });
    let eyebrow = isSuper ? 'Platform' : (me?.org_name || 'Your dealership');
    if (isSuper && currentNode) eyebrow = TYPE_LABEL[currentNode.type];
    let title = 'Team';
    if (isSuper) title = currentNode ? c.teamLabel : 'All users';
    let sub = 'Your dealership team · roles · and IronSight access.';
    if (isSuper) {
        sub = currentNode
            ? `${currentNode.name} and every organization beneath it.`
            : 'Everyone with an IronSight sign-in: Fusable staff, holding and dealer users.';
    }

    return (
        <>
            <SectionHead
                eyebrow={eyebrow}
                title={title}
                actions={(
                    <>
                        <Button variant="ghost" onClick={onRefresh}><RefreshIcon />Refresh</Button>
                        {c.canEdit && <Button variant="primary" onClick={onAdd}><PlusIcon />Add user</Button>}
                    </>
                )}
            >
                {sub}
            </SectionHead>

            <Alert>{c.actionError || c.error}</Alert>

            {!c.loading && (
                <div className="ad2-stats" role="group" aria-label="Quick filters">
                    {tiles.map((d) => (
                        <button
                            key={d.k}
                            type="button"
                            className="ad2-stat"
                            aria-pressed={filters.quick === d.k}
                            onClick={() => setFilters((f) => ({ ...f, quick: f.quick === d.k && d.k !== 'all' ? 'all' : d.k }))}
                        >
                            <span className="ad2-stat-n">{d.n}</span>
                            <span className="ad2-stat-l"><i className={`ad2-dot ad2-dot--${d.tone}`} aria-hidden="true" />{d.l}</span>
                        </button>
                    ))}
                </div>
            )}

            <div className="v2-board ad2-board">
                <div className="ad2-ctl">
                    <label className="ad2-search">
                        <span className="v2-sr">Search users</span>
                        <SearchIcon />
                        <input type="search" placeholder="Search name or email…" autoComplete="off" value={c.query} onChange={(e) => c.setQuery(e.target.value)} />
                    </label>
                    {isSuper && (
                        <select
                            className={`v2-sel ad2-sel${currentNode ? ' is-set' : ''}`}
                            aria-label="Organization"
                            title="Users of this organization and everything beneath it"
                            value={c.currentId || ''}
                            onChange={(e) => { c.setScope(e.target.value || null); c.setQuery(''); }}
                        >
                            <option value="">All organizations</option>
                            {c.orgOptions.map((o) => <option key={o.id} value={o.id}>{'  '.repeat(o.depth)}{o.name}</option>)}
                        </select>
                    )}
                    <select
                        className={`v2-sel ad2-sel${filters.role !== 'All' ? ' is-set' : ''}`}
                        aria-label="Role"
                        value={filters.role}
                        onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))}
                    >
                        <option value="All">All roles</option>
                        {(isSuper ? ['user', 'dealer_admin', 'super_admin'] : ['user', 'dealer_admin'])
                            .map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                    </select>
                    <select
                        className={`v2-sel ad2-sel${filters.status !== 'All' ? ' is-set' : ''}`}
                        aria-label="Status"
                        value={filters.status}
                        onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                    >
                        <option value="All">All statuses</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                    </select>
                    {any && <Button variant="link" onClick={clearAll}>Clear filters</Button>}
                    <span className="ad2-spacer" />
                    {!c.loading && <span className="ad2-count"><b>{rows.length}</b> of {c.users.length} users</span>}
                </div>
                {isSuper && currentNode && (
                    <div className="ad2-scope-line">
                        Showing <b>{currentNode.name}</b>
                        {currentNode.type === 'TOG'
                            ? ` and its ${(currentNode.children || []).filter((x) => x.type === 'COG').length} dealerships`
                            : ''}
                        .
                    </div>
                )}
                <DataTable
                    label="Users"
                    className="ad2-utbl"
                    columns={columns}
                    rows={rows}
                    getRowId={(u) => u.id}
                    onRowClick={onOpen}
                    selectedId={selectedId || undefined}
                    empty={empty}
                />
                {c.usersLoading && <div className="ad2-loading" role="status">Loading team…</div>}
                <div className="ad2-foot">
                    Users come from your Entra directory. “Reset password” emails a temporary password the user must change at next sign-in.
                </div>
            </div>
        </>
    );
}

UsersSection.propTypes = {
    c: PropTypes.object.isRequired,
    me: PropTypes.object,
    filters: PropTypes.object.isRequired,
    setFilters: PropTypes.func.isRequired,
    summaryOf: PropTypes.func.isRequired,
    onOpen: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
    onRefresh: PropTypes.func.isRequired,
    selectedId: PropTypes.string,
};

export default UsersSection;
